import React, { useEffect, useState } from 'react';
import { Button, Text, TextInput, View } from "../Components/Themed"
import { GroupApi, GroupHelpers } from './api';
import type { ArticleProps, CompetitionPayoutTypeProps, CompetitionPlayerProps, CompetitionProps, CompetitionRecordProps, CompetitionResultProps, CompetitionResultTypeProps, CompetitionTypeProps, EventProps, FocusPositionProps, GroupMessageProps, GroupPlayerProps, GroupProps, MyPlayerProps, NewOverallAnalytics, OrderAnalyticsProps, OrderProps, PollCampaignProps, PollProps, PostProps, PublicPlayerProps, SquaresCompetitionProps, TextTagProps } from '../types';
import { ActivityIndicator, FlatList, Image } from 'react-native';
import { Icons, Toggle } from '../Components';
import { useColors } from '../constants/useColors';
import moment from 'moment-mini';
import ManageGroupForm from './components/ManageGroupForm';
import GroupPlayerActionForm from './components/GroupPlayerActionForm';
import CompetitionCard from '../Engage/components/CompetitionCard';
import { PostCard } from '../SocialComponents';
import Pagination from '../Components/Pagination';
import GroupCTAButton from './components/GroupCTAButton';
import PollCampaignCard from '../Engage/components/PollCampaignCard';
import SquaresCompetitionCard from '../Engage/components/SquaresCompetitionCard';
import { showConfirmAlert } from '../Components/ConfirmAlert';
import MySettingsForm from './components/MySettingsForm';

type GroupComponentProps = {
    group_id:string,
    mode:'desktop'|'mobile',
    init_invite_code?:string,
    refresh_key:string,
    onSelectFlashMarket:(poll:PollProps) => void,
    onViewComments?:(post_id:string, post_type:string) => void,
    onFocusPosition:(pos:FocusPositionProps) => void,
    onSelectChat?:(group:GroupProps) => void
    onInvitePlayer:() => void,
    onSelectTag:(t:TextTagProps) => void,
    onCopyOrder?:(o:OrderProps) => void,
    onFadeOrder?:(o:OrderProps) => void,
    onSelectArticle:(a:ArticleProps) => void,
    onSelectImage:(i:any) => void,
    onNudgePlayer:(p:PublicPlayerProps, gp:GroupPlayerProps) => void,
    onSelectPlayer:(p:PublicPlayerProps) => void,
    onSelectCompetition:(c:CompetitionProps) => void,
    onRequestAuthenticate:() => void,
    onSelectPollCampaign:(pc:PollCampaignProps) => void,
    onSelectSquaresCompetition:(sq:SquaresCompetitionProps) => void,
    onCreateEngagement?: (g:GroupProps) => void,
    onShareGroup?:(g:GroupProps) => void,
    player?:MyPlayerProps
}
type GroupStateProps = {
    group?:GroupProps,
    loading:boolean,
    editing:boolean,
    invite_code:string,
    action_loading?:string,
    join_loading:boolean,
    show_player_status:boolean,
    active_toggle:string,
    stats_expanded:boolean,
    group_players:GroupPlayerProps[],
    players:PublicPlayerProps[],
    active_stat?: { stat_key:string, label:string, multiplier:number, prefix?:string, suffix?:string },
    group_messages: GroupMessageProps[],
    my_group_player?:GroupPlayerProps,
    editing_me:boolean
}
type TimeOptionProps = {
    to: number,
    from:number,
    option:string,
    label:string
}
const sections = ['header','chat','flash','toggle','me', 'competition_toggle', 'new_competition', 'members', 'competitions', 'activity']
const member_stat_options:{ stat_key:string, label:string, multiplier:number, prefix?:string, suffix?:string }[] = [
    { stat_key: 'roi_pct', label: 'ROI Percent', multiplier:100, suffix: '%' },
    //{ stat_key: 'winnings', label: 'Winnings', multiplier:1 , prefix: '$'},
    { stat_key: 'win_pct', label: 'Win Percent', multiplier: 100, suffix: '%' },
    //{ stat_key: 'fulfilled_positions', label: 'Matched Bets', multiplier:1, suffix: ' Bets' },
    //{ stat_key: 'original_stake', label: 'Betting Volume', multiplier: 1, prefix: '$' }
]

const GroupComponent = ({ refresh_key, group_id, player, mode, init_invite_code, onViewComments, onCopyOrder,onSelectFlashMarket, onFadeOrder, onSelectArticle, onSelectImage, onSelectTag, onCreateEngagement, onRequestAuthenticate, onFocusPosition, onShareGroup, onInvitePlayer, onSelectCompetition, onSelectChat, onSelectPlayer, onNudgePlayer, onSelectPollCampaign, onSelectSquaresCompetition } : GroupComponentProps) => {
    const Colors = useColors();
    const leader_options:TimeOptionProps[] = [
        { to: moment().subtract(1, 'days').unix(), from: moment().unix(), option: '24_hours', label: '24 Hours'  },
        { to: moment().subtract(7, 'days').unix(), from: moment().unix(), option: '7_days', label: '7 Days'  },
        { to: moment().subtract(30, 'days').unix(), from: moment().unix(), option: '30_days', label: '30 Days'  },
        { to: moment().subtract(60, 'days').unix(), from: moment().unix(), option: '60_days', label: '60 Days'  },
    ]
    const [ group_size, setGroupSize ] = useState({ height:0, width:0 });
    const [ activity_data, setActivityData ] = useState<{
        loading:boolean,
        orders:OrderProps[],
        posts:PostProps[],
        offset:number
    }>({
        loading: false,
        posts:[],
        orders:[],
        offset:0
    });
    const [ flash_data, setFlashData ] = useState<{
        expanded?:boolean,
        flash_campaigns:PollCampaignProps[],
        flash_markets:PollProps[]
    }>({
        flash_campaigns: [],
        expanded:false,
        flash_markets: []
    });
    const { expanded, flash_campaigns, flash_markets } = flash_data;
    const [ comp_data, setCompData ] = useState<{
        loading:boolean,
        toggle: 'upcoming'|'history',
        poll_campaigns:PollCampaignProps[],
        squares_competitions:SquaresCompetitionProps[],
        competition_types:CompetitionTypeProps[],
        events:EventProps[],
        competition_result_types:CompetitionResultTypeProps[],
        competition_payout_types:CompetitionPayoutTypeProps[],
        active_competitions:CompetitionProps[],
        competition_results:CompetitionResultProps[],
        competition_records:CompetitionRecordProps[],
        competition_players:CompetitionPlayerProps[],
        offset:number
    }>({
        loading:false,
        toggle: 'upcoming',
        competition_payout_types:[],
        competition_result_types:[],
        events:[],
        squares_competitions:[],
        poll_campaigns: [],
        competition_types:[],
        active_competitions: [],
        competition_players:[],
        competition_records:[],
        competition_results:[],
        offset:0
    });

    const [ group_analytics, setGroupAnalytics ] = useState<{
        loading:boolean,
        time_option?: TimeOptionProps,
        order_analytics:OrderAnalyticsProps[]
    }>({
        loading:false,
        time_option: leader_options[0],
        order_analytics: []
    });

    const { time_option, order_analytics } = group_analytics;
    const [ group_data, setGroupData ] = useState<GroupStateProps>({
        loading: false,
        group_messages: [],
        editing:false,
        editing_me: false,
        show_player_status:false,
        stats_expanded: false,
        invite_code: '',
        join_loading:false,
        active_toggle: 'members',
        active_stat: member_stat_options[0],
        group_players: [],
        players: []
    });
    const { loading, group, join_loading, action_loading, stats_expanded, active_toggle, editing_me, invite_code, editing,show_player_status, active_stat, group_messages, group_players, my_group_player, players } = group_data;
    const admin = player?.player_id == group?.group_admin ? true : false;
    const { sorted_players, player_analytics } = GroupHelpers.sortMembers(group_players, order_analytics, active_stat?.stat_key);
    const sorted_engagements = GroupHelpers.sortCompetitionAction([], comp_data.active_competitions, [], comp_data.squares_competitions, comp_data.poll_campaigns);
    useEffect(() => {
        getGroupData(group_id);
    },[group_id, refresh_key]);

    useEffect(() => {
        getToggleData(active_toggle);
    },[active_toggle])

    const getToggleData = async(toggle:string) => {
        switch(toggle){
            case 'members':
                if(!time_option){ return }
                return getGroupAnalytics(group_id, time_option);
            case 'competitions':
                return getCompetitionData(comp_data.toggle, 0);
            case 'activity':
                return getActitiyData(0);
            default: return
        }
    }
    const getActitiyData = async(page:number) => {
        setActivityData({ ...activity_data, loading: true });
        const activity = await GroupApi.getPostsByGroupId(group_id, page);
        let order_ids:string[] = []
        activity.filter(a => a.memo_orders).map(mo => mo.memo_orders.map(o => order_ids.push(o)));
        let activity_orders = await GroupApi.getBulkOrders(order_ids);
        setActivityData({
            ...activity_data,
            loading: false,
            offset: page,
            orders: activity_orders,
            posts: activity.sort((a,b) => moment(a.create_datetime).unix() - moment(b.create_datetime).unix())
        });
    }

    const getCompetitionData = async(type:'upcoming' | 'history', page:number) => {
        setCompData({ ...comp_data, loading:true });
        let resp:undefined | { competitions:CompetitionProps[], competition_players:CompetitionPlayerProps[], competition_records:CompetitionRecordProps[], competition_results:CompetitionResultProps[] } = {
            competitions:[], competition_players:[], competition_records:[], competition_results:[] }
        let server_polls:PollCampaignProps[] = []
        let server_sq: SquaresCompetitionProps[] = []
        let server_events:EventProps[] = []
        if(type == 'upcoming'){
            resp = await GroupApi.getActiveCompetitions(group_id);
            let flash_resp = await GroupApi.getActivePolls(group_id);
            server_polls = flash_resp.poll_campaigns.filter(pc => pc.market_type == 'FREE');
            server_sq = await GroupApi.getActiveSquares(group_id);
            let event_ids = server_sq.map(sq => sq.event_id);
            server_events = await GroupApi.getEventsByIds(event_ids);
        } else {
            resp = await GroupApi.getHistoryCompetitions(group_id, page);
            server_polls = await GroupApi.getHistoryPolls(group_id, page);
            server_sq = await GroupApi.getHistorySquares(group_id, page);
            let event_ids = server_sq.map(sq => sq.event_id);
            server_events = await GroupApi.getEventsByIds(event_ids);
        }
        const opts = await GroupApi.getCompetitionOptions();
        setCompData({
            ...comp_data,
            loading:false,
            toggle: type,
            poll_campaigns: server_polls.filter(p => p.market_type == 'FREE'),
            squares_competitions: server_sq,
            events: server_events,
            competition_players: resp.competition_players,
            competition_records: resp.competition_records,
            competition_results: resp.competition_results,
            competition_payout_types: opts.competition_payout_types,
            competition_result_types: opts.competition_result_types,
            competition_types: opts.competition_types,
            active_competitions: resp.competitions,
            offset:page
        })
    }

    const getGroupAnalytics = async(id:string, time_option:TimeOptionProps) => {
        setGroupAnalytics({ ...group_analytics, loading: true });
        let analytics = await GroupApi.getGroupAnalytics(id, time_option.to, time_option.from);
        setGroupAnalytics({
            ...group_analytics,
            loading:false,
            time_option: time_option,
            order_analytics: analytics
        })
    }

    const getGroupData = async(id:string) => {
        GroupApi.setEnvironment();
        setGroupData({ ...group_data, loading: true });
        const g_data = await GroupApi.getGroupById(id);
        if(!g_data){ 
            setGroupData({ ...group_data, loading: false });
            return alert('Unable to get group information.  Please try again later') 
        }
        let player_ids = [ ...new Set(g_data.group_players.map(p => p.player_id).concat(g_data.group_messages.map(m => m.player_id)))]
        let plyers = await GroupApi.getPlayersByIds(player_ids);
        let my_gp = g_data.my_group_player && g_data.my_group_player.status == 'inactive' ? undefined : g_data.my_group_player
        
        let server_flash = await GroupApi.getActivePolls(group_id);
        setFlashData({ flash_campaigns: server_flash.poll_campaigns, flash_markets: server_flash.polls.filter(p => !['pending','closed'].includes(p.status)) });

        setGroupData({
            ...group_data,
            loading: false,
            players: plyers,
            invite_code: init_invite_code ?? '',
            group: g_data.group,
            show_player_status: g_data.group.invite_only ? false : my_gp ? false : true, //Show to join if we can
            group_messages: g_data.group_messages,
            group_players: g_data.group_players,
            my_group_player: my_gp
        });


    }

    const renderPlayers = (data:{ item:GroupPlayerProps, index:number }) => {
        const pl = players.find(p => p.player_id == data.item.player_id);
        if(!pl){ return <></> }
        const player_analytic = player_analytics[data.item.player_id]
        const analytic = !data.item.leaderboard ? 0 : player_analytic ? player_analytic[active_stat?.stat_key as keyof NewOverallAnalytics] : 0
        const is_loading = action_loading == data.item.group_player_id ? true : false

        const show_activity_check = player?.player_id == data.item.player_id ? false : my_group_player?.status == 'active' && my_group_player.activity_notifications && data.item.activity ? true : false
        const following = my_group_player?.activity_notifications?.find(id => id == data.item.player_id) ? true : false

        return (
            <View transparent style={{ flexDirection:'row', alignItems:'center', padding:10, borderBottomWidth:1, borderColor:Colors.borders.light, borderRadius:0 }}>
                {admin && data.item.player_id != player?.player_id?
                <Button
                    title='X'
                    title_color={Colors.text.error}
                    loading={is_loading}
                    style={{ padding:10, opacity: is_loading ? 0.5 : 1 }}
                    onPress={() => {
                        showConfirmAlert('Are you sure?', 'This will boot and ban this player from the group.  You can unban the player later', 
                            async() => {
                                //Ok we have confirmed it!
                                let new_pl = await GroupApi.bootPlayer(data.item.group_player_id);
                                if(!new_pl){
                                    setGroupData({ ...group_data, action_loading: undefined });
                                    return alert('Unable to boot player at this time.  Please try again')
                                }
                                setGroupData({
                                    ...group_data,
                                    action_loading: undefined,
                                    group_players: group_players.filter(gp => gp.group_player_id != new_pl.group_player_id).concat(new_pl)
                                })
                            }
                        ),
                        () => { console.log('cancelled') }
                    }}
                />
                :<></>}
                <Button 
                    disabled={!show_activity_check || my_group_player?.player_id == data.item.player_id}
                    onPress={async() => {
                        if(!my_group_player){ return }
                        let new_activity = my_group_player.activity_notifications ?? []
                        if(!following){ new_activity = new_activity.concat(data.item.player_id) }
                        else { new_activity = new_activity.filter(id => id != data.item.player_id) }
                        let new_gp = await GroupApi.updateGroupPlayer({ ...my_group_player, activity_notifications: new_activity });
                        if(!new_gp){ return alert('Unable to update') }
                        setGroupData({
                            ...group_data,
                            group_players: group_players.filter(gp => gp.group_player_id != new_gp?.group_player_id).concat(new_gp),
                            my_group_player: new_gp
                        })
                    }}
                    float
                    style={{ borderRadius:100, padding:2, borderColor:following?Colors.text.success:show_activity_check?Colors.text.warning:Colors.borders.light }}>
                    <Image
                        source={{ uri: pl.profile_pic }}
                        style={{ height:45, width:45, borderRadius:100 }}
                        resizeMode='contain'
                    />
                </Button>
                <Button transparent style={{ flex:1, marginLeft:10, padding:0 }}  onPress={() => onSelectPlayer(pl)}>
                    <Text theme='h1'>@{pl.username}</Text>
                    <Text theme='description' style={{ marginTop:3 }}>{data.item.status == 'active' ? 'Joined' : 'Invited'} {moment(data.item.last_update_datetime).fromNow()}</Text>
                </Button>
                {active_stat && player_analytic && data.item.status == 'active' ?
                <View style={{ alignItems:'flex-end' }}>
                    {data.item.leaderboard ?
                    <Text theme='h1'>{active_stat.prefix}{(parseFloat(analytic as string) * active_stat.multiplier).toFixed(2)}{active_stat.suffix}</Text>
                    :
                    <Icons.LockClosedIcon size={14} color={Colors.text.h1} />
                    }
                    <Text theme='light' style={{ marginTop:3 }}>{active_stat.label}</Text>
                </View>
                :<></>}
                {data.item.status == 'invited' ?
                <Button
                    disabled={!admin}
                    type='text'
                    style={{ padding:10, opacity:is_loading ? 0.5: 1 }}
                    loading={is_loading}
                    title_color={Colors.text.action}
                    title={admin?'Nudge':'Invited'}
                    onPress={() => onNudgePlayer(pl, data.item)}
                />
                :<></>}
                {admin && data.item.status == 'invited' ?
                <Button
                    transparent
                    style={{ padding:10, opacity: is_loading ? 0.5:1 }}
                    title_color={Colors.text.error}
                    loading={is_loading}
                    title='Uninvite'
                    onPress={async() => {
                        setGroupData({ ...group_data, action_loading: data.item.group_player_id });
                        const new_pl = await GroupApi.uninvitePlayer(data.item.group_player_id);
                        if(!new_pl){
                            setGroupData({ ...group_data, action_loading: undefined });
                            return alert('Unable to uninvite player.  Please try again')
                        }
                        setGroupData({
                            ...group_data,
                            action_loading: undefined,
                            group_players: group_players.filter(gp => gp.group_player_id != new_pl.group_player_id).concat(new_pl)
                        })
                    }}
                />
                :<></>}
            </View>
        )
    }

    const renderFlash = (data: { item: PollProps, index:number }) => {
        return (
            <View transparent style={{ flexDirection:'row', alignItems:'center', padding:10, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                {data.item.poll_image?.url ?
                <Image
                    source={{ uri: data.item.poll_image.url }}
                    style={{ height:30, width: 30 }}
                    resizeMode='contain'
                />
                :
                <Icons.FlameIcon size={30} color={Colors.text.error} />
                }
                <View transparent style={{ flex:1, marginLeft:10 }}>
                    <Text theme='h1'>{data.item.poll_question}</Text>
                    <Text theme='description' style={{ marginTop:3 }}>{data.item.status.toUpperCase()}</Text>
                </View>
                <Button
                    style={{ padding:10, marginLeft:5 }}
                    type={data.item.status == 'active' ? 'success' : 'action'}
                    title={data.item.status == 'active' ? 'BUY' : 'VIEW'}
                    onPress={() => onSelectFlashMarket(data.item)}
                />
            </View>
        )
    }

    const renderEngagements = (data: { item:{id:string, type:string, scheduled_datetime:any}, index: number }) => {
        switch (data.item.type){
            case 'competition':
                const competition = comp_data.active_competitions.find(c => c.competition_id == data.item.id);
                if(!competition){ return <></> }
                const type = comp_data.competition_types.find(t => t.competition_type_id == competition.competition_type_id);
                const result = comp_data.competition_result_types.find(r => r.competition_result_type_id == competition.competition_result_type_id);
                if(!type || !result){ return <></> }
                const my_result = comp_data.competition_results.find(cr => cr.player_id == player?.player_id && cr.competition_id == competition.competition_id)
                const my_record = comp_data.competition_records.find(cr => cr.player_id == player?.player_id && cr.competition_id == competition.competition_id)
                return (
                    <View style={{ padding:5 }}>
                        <CompetitionCard
                            competition={competition}
                            player={player}
                            competition_type={type}
                            competition_record={my_record}
                            competition_result={my_result}
                            competition_result_type={result}
                            onCompetitionSelect={onSelectCompetition}
                        />
                    </View>
                )
            case 'bracket':
                return <></>
            case 'squares':
                const squares_comp = comp_data.squares_competitions.find(sc => sc.sq_comp_id == data.item.id);
                if(!squares_comp){ return <></> }
                const event = comp_data.events.find(e => e.event_id == squares_comp.event_id);
                return (
                    <View style={{ padding:5 }}>
                        <SquaresCompetitionCard
                            squares_competition={squares_comp}
                            event={event}
                            onSelectCompetition={onSelectSquaresCompetition}
                        />
                    </View>
                )
            case 'poll_campaign':
                const poll_campaign = comp_data.poll_campaigns.find(pc => pc.poll_campaign_id == data.item.id);
                if(!poll_campaign){ return <></> }
                return (
                    <View style={{ padding:5 }}>
                        <PollCampaignCard
                            poll_campaign={poll_campaign}
                            onSelectPollCampaign={onSelectPollCampaign}
                        />
                    </View>
                )
            default:return <></>
        }
    }

    const renderPosts = (data:{ item:PostProps, index:number }) => {
        let post_player = players.find(p => p.player_id == data.item.player_id);
        let orders = activity_data.orders.filter(o => data.item.memo_orders.includes(o.order_id));
        return (
            <View style={{ padding:5 }}>
                <PostCard
                    post={data.item}
                    player={post_player}
                    player_id={player?.player_id}
                    orders={orders}
                    onRequestAuthenticate={() => console.log('')}
                    onSelectArticle={onSelectArticle}
                    articles={[]}
                    width={group_size.width}
                    onViewComments={onViewComments ? () => onViewComments(data.item.memo_post_id, 'memo') : undefined}
                    onCopyOrder={onCopyOrder}
                    onFadeOrder={onFadeOrder}
                    onSelectImage={onSelectImage}
                    onSelectPlayer={onSelectPlayer}
                    onSelectTag={onSelectTag}
                />
            </View>
        )
    }

    const renderSections = (data:{ item:string, index:number }) => {
        switch(data.item){
            case 'header':
                if(!group){ return <></> }
                return (
                    <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                        <Image
                            source={{ uri: group.group_image?.url }}
                            style={{ height:50, width: 50 }}
                            resizeMode='cover'
                        />
                        <View transparent style={{ flex:1, marginLeft:10, marginRight:10 }}>
                            <Text theme='h1'>{group.name}</Text>
                            <Text theme='description' style={{ marginTop:3 }}>{group.description}</Text>
                        </View>
                        {onShareGroup ?
                        <Button float style={{ padding:10 }} onPress={() => onShareGroup(group)}>
                            <Icons.ShareIcon size={14} color={Colors.text.action} />
                        </Button>
                        :<></>}
                        {player?.player_id == group.group_admin ?
                        <Button float style={{ padding:10, marginLeft:10 }} onPress={() => setGroupData({ ...group_data, editing:true })}>
                            <Icons.EditIcon size={14} color={Colors.text.h1} />
                        </Button>
                        :
                        <Button
                            type='success'
                            style={{ padding:10, marginLeft:10 }}
                            title='INVITE'
                            onPress={() => onInvitePlayer()}
                        />
                        }
                    </View>
                )
            case 'chat':
                if(!group || !onSelectChat){ return <></> }
                return (
                    <View type='header' style={{ padding:10, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                        <GroupCTAButton 
                            group={group}
                            hide_from_player
                            message_length={20}
                            players={players}
                            group_messages={group_messages}
                            group_players={group_players}
                            onSelectChat={onSelectChat}
                        />
                    </View>
                )
            case 'flash':
                if(flash_campaigns.length == 0){ return <></> }
                if(flash_markets.length == 0){ return <></> }
                return (
                    <View float style={{ margin:10 }}>
                        <Button style={{ backgroundColor:Colors.views.header, flexDirection:'row', alignItems:'center', padding:10, borderBottomLeftRadius:expanded?0:8, borderBottomRightRadius:expanded?0:8 }} onPress={() => setFlashData({ ...flash_data, expanded: !expanded })}>
                          <Icons.FlameIcon size={35} color={Colors.text.error} />
                          <View transparent style={{ flex:1, marginLeft:10 }}>
                            <Text theme='h1'>Flash Markets Available!</Text>
                            <Text theme='description' style={{ marginTop:3 }}>Press here to see all flash markets for this group</Text>
                          </View>
                          <View float style={{ borderRadius:100, height:25, width:25, justifyContent:'center', alignItems:'center' }}>
                            <Text theme='h1'>{flash_markets.length}</Text>
                          </View>
                        </Button>
                        {expanded ?
                        <View transparent>
                            <FlatList
                                data={flash_markets.sort((a,b) => {
                                    let a_status = a.status == 'active' ? 2 : a.status == 'paused' ? 1 : 0
                                    let b_status = b.status == 'active' ? 2 : b.status == 'paused' ? 1 : 0
                                    return b_status - a_status || moment(a.end_datetime).unix() - moment(b.end_datetime).unix()
                                })}
                                renderItem={renderFlash}
                                key={'flash_markets_group'}
                                keyExtractor={item => item.poll_campaign_id.toString()}
                            />
                        </View>
                        :<></>}
                    </View>
                )
            case 'new_competition':
                if(active_toggle != 'competitions' || !group){ return <></> }
                if(!onCreateEngagement){ return <></> }
                return (
                    <View type='footer' style={{ flexDirection:'row', alignItems:'center', padding:10 }}>
                        <View transparent style={{ flex:1, marginRight:10 }}>
                            <Text theme='h2'>CREATE A NEW ENGAGEMENT</Text>
                            <Text theme='description' style={{ marginTop:3 }}>Create a new pick-em or other engagement for this group!</Text>
                        </View>
                        <Button
                            type='success'
                            style={{ padding:10 }}
                            title='CREATE'
                            onPress={() => onCreateEngagement(group)}
                        />
                    </View>
                )
            case 'me':
                if(!group) { return <></> }
                if(active_toggle != 'members'){ return <></> }
                return (
                    <View type='row' style={{ padding:10 }}>
                        {player ?
                        <View style={{ flex:1 }} type='row' transparent>
                            <View float style={{ borderRadius:100, padding:2 }}>
                                <Image
                                    style={{ height:30, width:30, borderRadius:100 }}
                                    source={{ uri: player.profile_pic }}
                                    resizeMode='contain'
                                />
                            </View>
                            <View transparent style={{ flex:1, marginLeft:10 }}>
                                <Text theme='h1'>@{player.username}</Text>
                                <Text theme='description' style={{ marginTop:3 }}>Member since {moment(player.create_datetime).format('MM/DD/YYYY')}</Text>
                            </View>
                            {my_group_player?.status == 'active' ? 
                            <Button float style={{ padding:10, marginRight:10 }} onPress={() => setGroupData({ ...group_data, editing_me: true })}>
                                <Icons.SettingsIcon size={12} color={Colors.text.h1} />
                            </Button>
                            :<></>}
                            {my_group_player?.status == 'invited' ?
                                <Button
                                    type='action'
                                    title='INVITED'
                                    style={{ padding:10 }}
                                    onPress={() => setGroupData({ ...group_data, show_player_status: true })}
                                />
                            :<></>}
                            {!group?.invite_only && (!my_group_player || my_group_player.status == 'inactive') ?
                                <Button
                                    type='success'
                                    title='JOIN'
                                    style={{ padding:10 }}
                                    onPress={() => setGroupData({ ...group_data, show_player_status: true })}
                                />
                            : my_group_player?.status != 'invited' && !admin ?
                             <Button
                                type='error'
                                title='LEAVE'
                                style={{ padding:10 }}
                                onPress={() => setGroupData({ ...group_data, show_player_status: true })}
                            />
                            :my_group_player?.status != 'invited' && admin ?
                            <Text theme='h1' color={Colors.text.gold}>ADMIN</Text>
                            :<></>}
                        </View>
                        :<></>}
                    </View>
                )
            case 'toggle':
                return (
                    <View style={{ padding:10 }}>
                        <Toggle
                            options={[
                                { key: 'members', label: 'Members' },
                                { key: 'activity', label: 'Activity' },
                                { key: 'competitions', label: 'Engagements' }
                            ]}
                            selected_option={active_toggle}
                            onSelectOption={(option) => setGroupData({ ...group_data, active_toggle: option })}
                        />
                        {loading ?
                        <ActivityIndicator
                            size='large'
                            color={Colors.text.h1}
                            style={{ padding:20, alignSelf:'center' }}
                        />
                        :<></>}
                    </View>
                )
            case 'activity':
                if(active_toggle != 'activity'){ return <></> }
                return (
                    <View style={{ padding:10 }}>
                        {activity_data.loading ?
                        <ActivityIndicator style={{ padding:20, alignSelf:'center' }} size={'large'} color={Colors.text.h1} />
                        :<></>}
                        <FlatList
                            data={activity_data.posts.sort((a,b) => moment(b.create_datetime).unix() - moment(a.create_datetime).unix())}
                            key={'post_list'}
                            keyExtractor={item => item.memo_post_id.toString()}
                            renderItem={renderPosts}
                        />
                    </View>
                )
            case 'members':
                if(active_toggle != 'members'){ return <></> }
                return (
                    <View>
                        <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, zIndex: 10 }}>
                            <View transparent style={{ flex:1, marginRight:10 }}>
                                <Text theme='h1'>Group Members</Text>
                                <Text theme='description'>Active and invited members are show below.  You can sort the members by different leaderboards using the dropdown on the right.</Text>
                            </View>
                            <View float>
                                <Button transparent style={{ flexDirection:'row', alignItems:'center', padding:10 }} onPress={() => setGroupData({ ...group_data, stats_expanded: !stats_expanded })}>
                                    <Text style={{ marginRight:5 }} theme='h2'>{active_stat?.label ?? 'Select'}</Text>
                                    <Icons.ChevronIcon direction={stats_expanded?'up':'down'} color={Colors.text.h1} size={8} />
                                </Button>
                                {stats_expanded ?
                                <View float style={{ position:'absolute', top:0, right:0, width:200 }}>
                                    {member_stat_options.map(so => {
                                        return (
                                            <Button transparent style={{ padding:10, borderRadius:0, borderBottomWidth:1, borderColor:Colors.borders.light }} onPress={() => setGroupData({ ...group_data, active_stat: so, stats_expanded: false })}>
                                                <Text theme='h1'>{so.label}</Text>
                                            </Button>
                                        )
                                    })}
                                </View>
                                :<></>}
                            </View>
                        </View>
                        <View style={{ padding:10}}>
                        <Toggle
                            options={leader_options.map(o => { return { key: o.option, label: o.label } })}
                            selected_option={time_option?.option}
                            onSelectOption={(option) => {
                                const selected = leader_options.find(o => o.option == option)
                                if(!selected){ return }
                                getGroupAnalytics(group_id, selected);
                            }}
                        />
                        </View>
                        {my_group_player && my_group_player.activity_notifications ? 
                        <View style={{ padding:10 }}>
                            <Text theme='description' color={Colors.text.warning}>If a player is sharing their activity, their picture is outlined in orange. You can select the player's profile picture to get notified of their activity.</Text>
                        </View>  
                        :<></>}
                        <View style={{ padding:10 }}>
                            <FlatList
                                data={sorted_players}
                                keyExtractor={item => item.group_player_id.toString()}
                                renderItem={renderPlayers}
                                initialNumToRender={10}
                                windowSize={4}
                                key={'group_members'}
                            />
                        </View>
                    </View>
                )
            case 'competition_toggle':
                if(active_toggle != 'competitions'){ return <></> }
                return (
                    <View style={{ padding:10 }}>
                        <Toggle
                            options={[
                                { key: 'upcoming', label: 'Upcoming' },
                                { key: 'history', label: 'History' }
                            ]}
                            selected_option={comp_data.toggle}
                            onSelectOption={(option) => getCompetitionData(option as 'upcoming' | 'history', 0)}
                        />
                    </View>
                )
            case 'competitions':
                if(active_toggle != 'competitions'){ return <></> }
                return (
                    <View transparent style={{ padding:10 }}>
                        {comp_data.loading ?
                        <ActivityIndicator style={{ padding:20, alignSelf:'center' }} size={'large'} color={Colors.text.h1} />
                        :<></>}
                        <FlatList
                            key={'competition_list'}
                            keyExtractor={item => item.id.toString()}
                            data={sorted_engagements.sort((a,b) => moment(a.scheduled_datetime).unix() - moment(b.scheduled_datetime).unix())}
                            renderItem={renderEngagements}
                        />
                    </View>
                )
            default: return <></>
        }
    }

    return (
        <View style={{ flex:1 }} onLayout={(ev) => {
            const { height, width } = ev.nativeEvent.layout;
            setGroupSize({ height, width })
        }}>
            <View style={{ flex:1 }}>
                <FlatList
                    data={sections}
                    key={'group_list'}
                    refreshing={loading}
                    onRefresh={() => getGroupData(group_id)}
                    keyExtractor={item => item}
                    renderItem={renderSections}
                />
                {active_toggle == 'members' ?
                <></>
                :active_toggle == 'activity' ?
                <Pagination
                    offset={activity_data.offset}
                    onNext={() => getActitiyData(activity_data.offset + 1)}
                    onPrevious={() => getActitiyData(activity_data.offset - 1)}
                />
                :<></>}
            </View>
            {editing && group ?
            <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0 }}>
                <View type='blur' style={mode == 'desktop'?{ alignSelf:'center', width: 500, height:500 }: { flex:1, justifyContent:'flex-end', padding:10 }}>
                    <ManageGroupForm
                        group={group}
                        onFocusPosition={onFocusPosition}
                        onInvitePlayer={() => {
                            setGroupData({ ...group_data, editing: false })
                            onInvitePlayer();
                        }}
                        onClose={() => setGroupData({ ...group_data, editing: false })}
                        onUpdateGroup={(g) => setGroupData({ ...group_data, group: g, editing: g.status == 'archived' ? false : true })}
                    />
                </View>
            </View>
            :<></>}
            {editing_me && group && my_group_player ?
            <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0 }}>
                <View type='blur' style={mode == 'desktop'?{ alignSelf:'center', justifyContent:'center', width: 500, height:500 }: { flex:1, justifyContent:'flex-end', padding:10 }}>
                    <MySettingsForm
                        group_player={my_group_player}                        
                        onClose={() => setGroupData({ ...group_data, editing_me: false })}
                        onUpdateGroupPlayer={(gp) => setGroupData({ 
                            ...group_data,
                            group_players: group_players.filter(p => p.group_player_id != gp.group_player_id).concat(gp),
                            my_group_player: { ...gp }
                        })}
                    />
                </View>
            </View>
            :<></>}
            {show_player_status && group && player ?
            <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0 }}>
                <View type='blur' style={mode == 'desktop'?{ justifyContent:'center', alignSelf:'center', width: 500, height:500 }: { flex:1, justifyContent:'flex-end', padding:10 }}>
                    <GroupPlayerActionForm
                        group={group}
                        group_player={my_group_player}
                        me={player}
                        onRequestAuthenticate={onRequestAuthenticate}
                        onResponse={(new_gp) => {
                            setGroupData({
                                ...group_data,
                                group_players: group_players.filter(gp => gp.group_player_id != new_gp.group_player_id).concat(new_gp),
                                my_group_player: new_gp.status == 'inactive' ? undefined: new_gp,
                                show_player_status: false
                            })
                        }}
                        onClose={() => setGroupData({ ...group_data, show_player_status: false })}
                    />
                </View>
            </View>
            :<></>}
            {group && group.status == 'archived' ?
            <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0, justifyContent:'center', alignItems:'center' }}>
                <View float style={{ padding:10 }}>
                    <Text theme='h1' textAlign='center'>GROUP NOT ACTIVE</Text>
                    <Text theme='description' textAlign='center' style={{ marginTop:5 }}>This group have been deleted by the admin</Text>
                </View>
            </View>
            :<></>}
            {group && group.invite_only && !my_group_player ?
            <View type='dark_blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0, justifyContent:'flex-end', margin:10 }}>
                <View float>
                    <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                        <Image
                            source={{ uri: group.group_image?.url }}
                            style={{ height:50, width:50 }}
                            resizeMode='cover'
                        />
                        <View transparent style={{ flex:1, marginLeft:10, marginRight:10 }}>
                            <Text theme='h1'>{group.name}</Text>
                            <Text theme='warning' style={{ marginTop:3 }}>This is a private group</Text>
                        </View>
                        <Icons.LockClosedIcon size={20} color={Colors.text.error} />
                    </View>
                    <View transparent style={{ padding:20 }}>
                        <Text theme='h1'>Have an Invite Code?</Text>
                        <View transparent type='row' style={{ marginTop:10 }}>
                            <TextInput
                                style={{flex:1 }}
                                onFocusPosition={onFocusPosition}
                                placeholder='Enter Code Here...'
                                value={invite_code}
                                onChangeText={(code) => setGroupData({ ...group_data, invite_code: code })}
                            />
                            <Button
                                type='success'
                                title='JOIN'
                                disabled={!invite_code}
                                style={{ marginLeft:5, opacity: join_loading || !invite_code ? 0.5: 1 }}
                                loading={join_loading}
                                onPress={async() => {
                                    if(!player){ return onRequestAuthenticate() }
                                    setGroupData({ ...group_data, join_loading:true });
                                    let new_player = await GroupApi.join(group_id, invite_code);
                                    if(!new_player){
                                        alert('The code entered is not valid.  Please reach out to the admin for the correct code');
                                        return setGroupData({ ...group_data, join_loading: false })
                                    }
                                    setGroupData({
                                        ...group_data,
                                        join_loading: false
                                    });
                                    getGroupData(group_id);
                                    //try to join this group!
                                }}
                            />
                        </View>
                    </View>
                </View>
            </View>
            :<></>}
        </View>
    )
}

export default GroupComponent